import i18next from "i18next";
import { z } from "zod";
export const SuccessorType = z.enum([
    "person",
    "orgFunction",
    "system",
    "externalContact",
    "unclear",
    "none"
]);
const SELECTABLE_MAPPINGS = {
    person: {
        idField: "personId",
        collection: "persons",
        messageKey: "errors.validation.selectPerson"
    },
    orgFunction: {
        idField: "orgFunctionId",
        collection: "orgFunctions",
        messageKey: "errors.validation.selectOrgFunction"
    },
    system: {
        idField: "systemId",
        collection: "systems",
        messageKey: "errors.validation.selectSystem"
    },
    externalContact: {
        idField: "externalContactId",
        collection: "externalContacts",
        messageKey: "errors.validation.selectExternalContact"
    }
};
// OrgFunctions and systems carry the EntityOrSuggestionRef shape (a successor may
// be a pending suggestion); persons and externalContacts stay as bare ids
// because those classes don't go through the suggestion flow.
const EMPTY_COLLECTIONS = {
    persons: [],
    orgFunctions: [],
    systems: [],
    externalContacts: []
};
export const SuccessorFormDataSchema = z.object({
    type: SuccessorType,
    personId: z.string().optional(),
    orgFunctionId: z.string().optional(),
    systemId: z.string().optional(),
    externalContactId: z.string().optional()
}).superRefine((data, ctx)=>{
    const mapping = SELECTABLE_MAPPINGS[data.type];
    if (!mapping) return;
    if (!data[mapping.idField]) {
        ctx.addIssue({
            code: "custom",
            message: i18next.t(mapping.messageKey),
            path: [
                mapping.idField
            ]
        });
    }
});
// Form-side `personId`/`orgFunctionId`/`systemId`/`externalContactId` are always
// resolved entity ids (the form's pickers don't surface pending suggestions).
// Wrap orgFunctions/systems into the union-ref shape the contract expects on output.
function wrapEntryForCollection(collection, id) {
    if (collection === "orgFunctions" || collection === "systems") {
        return {
            type: "entity",
            id
        };
    }
    return id;
}
export function toSuccessorUpdate(formData) {
    if (formData.type === "unclear" || formData.type === "none") {
        return {
            successorStatus: formData.type === "unclear" ? "UNCLEAR" : "NONE",
            ...EMPTY_COLLECTIONS
        };
    }
    const mapping = SELECTABLE_MAPPINGS[formData.type];
    const id = formData[mapping.idField];
    if (!id) return {};
    const entry = wrapEntryForCollection(mapping.collection, id);
    // Each collection is narrowly typed in StepSuccessors; the wrapper guarantees
    // the entry shape matches its collection (string for persons/externalContacts,
    // EntityOrSuggestionRef for teams/systems).
    return {
        [mapping.collection]: [
            entry
        ],
        successorStatus: ""
    };
}
/**
 * Merge a new successor into an existing value (additive). Used by the
 * "add another" flow where the dialog appends rather than replacing.
 */ export function handleAddSuccessor(formData, updates, value) {
    if (formData.type === "unclear" || formData.type === "none") return updates;
    const mapping = SELECTABLE_MAPPINGS[formData.type];
    const id = formData[mapping.idField];
    if (!id) return updates;
    const entry = wrapEntryForCollection(mapping.collection, id);
    const existing = value[mapping.collection] ?? [];
    updates[mapping.collection] = [
        ...existing,
        entry
    ];
    return updates;
}

//# sourceMappingURL=successorFormSchema.js.map